/**
 * Pull-to-refresh (spec §9 item 2) as a Svelte action on the scrolling list. Only while the
 * list is scrolled to the top and the viewport is the mobile layout: the indicator height is
 * `drag × 0.45` capped at 90 px; releasing past 55 px runs `onRefresh()` while the caller
 * shows the 52 px spinner.
 */
export const PTR_RATIO = 0.45;
export const PTR_MAX = 90;
export const PTR_TRIGGER = 55;
export const PTR_SPINNER_H = 52;

export interface PullOptions {
  enabled: () => boolean;
  onPull: (heightPx: number) => void;
  onRelease: (heightPx: number) => void;
}

export function pullHeight(dragPx: number): number {
  return Math.min(PTR_MAX, Math.max(0, dragPx * PTR_RATIO));
}

export function pullToRefresh(node: HTMLElement, opts: PullOptions): { destroy(): void } {
  let startY = 0;
  let pulling = false;
  let height = 0;

  const start = (ev: TouchEvent) => {
    if (!opts.enabled() || node.scrollTop > 0 || ev.touches.length !== 1) return;
    startY = ev.touches[0]!.clientY;
    pulling = true;
    height = 0;
  };
  const move = (ev: TouchEvent) => {
    if (!pulling) return;
    const dy = ev.touches[0]!.clientY - startY;
    if (dy <= 0 || node.scrollTop > 0) {
      if (height) {
        height = 0;
        opts.onPull(0);
      }
      return;
    }
    height = pullHeight(dy);
    opts.onPull(height);
  };
  const end = () => {
    if (!pulling) return;
    pulling = false;
    const h = height;
    height = 0;
    opts.onRelease(h);
  };
  const cancel = () => {
    if (!pulling) return;
    pulling = false;
    height = 0;
    opts.onPull(0);
  };
  node.addEventListener('touchstart', start, { passive: true });
  node.addEventListener('touchmove', move, { passive: true });
  node.addEventListener('touchend', end);
  node.addEventListener('touchcancel', cancel);
  return {
    destroy() {
      node.removeEventListener('touchstart', start);
      node.removeEventListener('touchmove', move);
      node.removeEventListener('touchend', end);
      node.removeEventListener('touchcancel', cancel);
    },
  };
}
